import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Clock, Percent, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FloatingSummaryBarProps {
  annualImpact: number;
  paybackMonths: number;
  yearOneROI: number;
  formatCurrency: (value: number) => string;
  language: string;
  visible: boolean;
  onScrollToTop?: () => void;
}

const FloatingSummaryBar = ({
  annualImpact,
  paybackMonths,
  yearOneROI,
  formatCurrency,
  language,
  visible,
  onScrollToTop
}: FloatingSummaryBarProps) => {
  const tr = {
    annualImpact: language === 'sv' ? 'Årlig påverkan' : 'Annual Impact',
    payback: language === 'sv' ? 'Återbetalning' : 'Payback',
    months: language === 'sv' ? 'mån' : 'mo',
    roi: language === 'sv' ? 'År 1 ROI' : 'Year 1 ROI'
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="sticky bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur-md border-t shadow-lg px-3 py-2"
        >
          <div className="flex items-center justify-between gap-2 max-w-4xl mx-auto">
            {/* Key metrics */}
            <div className="flex items-center gap-3 sm:gap-6 flex-1">
              <div className="flex items-center gap-1.5">
                <div className="p-1 bg-brand-100 dark:bg-brand-900/30 rounded">
                  <TrendingUp className="h-3 w-3 text-brand-600 dark:text-brand-400" />
                </div>
                <div className="hidden xs:block">
                  <p className="text-[9px] text-muted-foreground leading-tight">{tr.annualImpact}</p>
                  <p className="text-xs sm:text-sm font-bold text-brand-600">{formatCurrency(annualImpact)}</p>
                </div>
                <p className="xs:hidden text-xs font-bold text-brand-600">{formatCurrency(annualImpact)}</p>
              </div>

              <div className="h-6 w-px bg-border hidden sm:block" />

              <div className="flex items-center gap-1.5">
                <div className="p-1 bg-green-100 dark:bg-green-900/30 rounded">
                  <Clock className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <div className="hidden xs:block">
                  <p className="text-[9px] text-muted-foreground leading-tight">{tr.payback}</p>
                  <p className="text-xs sm:text-sm font-bold text-green-600">
                    {paybackMonths < 1 ? '<1' : paybackMonths.toFixed(1)} {tr.months}
                  </p>
                </div>
                <p className="xs:hidden text-xs font-bold text-green-600">
                  {paybackMonths < 1 ? '<1' : paybackMonths.toFixed(1)}{tr.months}
                </p>
              </div>

              <div className="h-6 w-px bg-border hidden sm:block" />

              <div className="flex items-center gap-1.5">
                <div className="p-1 bg-purple-100 dark:bg-purple-900/30 rounded">
                  <Percent className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="hidden xs:block">
                  <p className="text-[9px] text-muted-foreground leading-tight">{tr.roi}</p>
                  <p className="text-xs sm:text-sm font-bold text-purple-600">{yearOneROI.toFixed(0)}%</p>
                </div>
                <p className="xs:hidden text-xs font-bold text-purple-600">{yearOneROI.toFixed(0)}%</p>
              </div>
            </div>

            {/* Scroll to top button */}
            {onScrollToTop && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onScrollToTop}
                className="h-8 w-8 p-0 shrink-0"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingSummaryBar;
